
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { PageTitle } from "@/components/PageTitle";
import { SectionTitle } from "@/components/SectionTitle";
import { CTASection } from "@/components/CTASection";
import { Button } from "@/components/ui/button";
import { ArrowRight, Check } from "lucide-react";
import { DoctorCard } from "@/components/DoctorCard";

const About = () => {
  const navigate = useNavigate();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const scrollToTeam = () => {
    const teamSection = document.getElementById('team-section');
    if (teamSection) {
      teamSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="flex flex-col min-h-screen">
      <PageTitle title="About – Asrani Dental Clinic" />
      <Header />
      
      {/* Hero Section */}
      <section className="pt-32 pb-20 md:pt-40 md:pb-32 bg-dental-offwhite">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center animate-fade-in">
            <span className="inline-block text-dental-highlight font-medium mb-4 uppercase tracking-wider text-sm">
              About Us
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif font-semibold mb-6">
              Our Story and Mission
            </h1>
            <p className="text-lg text-dental-muted">
              Founded on the principles of exceptional care and patient comfort, Asrani Dental has been serving our community with distinction for over three decades.
            </p>
          </div>
        </div>
      </section>
      
      {/* Story Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1 animate-fade-in-up">
              <SectionTitle
                subtitle="Our Story"
                title="A Journey of Excellence and Compassion"
                description=""
                alignment="left"
                className="mb-6"
              />
              
              <p className="text-dental-muted mb-6">
                Asrani Dental was established in 1986 by Dr. Hemant Asrani with a visionary goal: to create a dental practice that seamlessly integrates clinical excellence with heartfelt compassion and patient-centered care.
              </p>
              
              <p className="text-dental-muted mb-6">
                What began as a single practice has flourished into a trusted, multi-center provider of exceptional dental care, renowned for our personalized approach and unwavering commitment to creating positive dental experiences. While we have expanded our team and services over the years, our core values of integrity, excellence, and patient-first care have remained at the heart of everything we do.
              </p>
              
              <p className="text-dental-muted mb-8">
                Today, we take immense pride in offering comprehensive dental solutions for patients of all ages, ensuring that world-class dental care is accessible, comfortable, and highly effective. Our cutting-edge facilities are equipped with state-of-the-art technology, but it is our dedicated team's expertise, passion, and personalized touch that truly define the Asrani Dental experience.
              </p>
              
              <Button 
                className="btn-primary flex items-center text-base group"
                onClick={scrollToTeam}
              >
                Meet Our Team
                <ArrowRight size={18} className="ml-2 transition-transform duration-300 group-hover:translate-x-1" />
              </Button>
            </div>
            
            <div className="order-1 lg:order-2 animate-fade-in">
              <div className="relative">
                <div className="w-full h-full absolute -top-6 -right-6 rounded-2xl border-2 border-dental-accent/20"></div>
                <img 
                  src="/lovable-uploads/b8b105b4-d7d4-4dd7-a804-c7f3b4e5b144.png" 
                  alt="Dr. Hemant Asrani in clinic with 'Shaping smiles since 1986' sign" 
                  className="w-full h-auto rounded-2xl shadow-soft"
                />
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Vision & Mission Section */}
      <section className="py-16 md:py-24 bg-dental-cream">
        <div className="container-custom">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            <div className="bg-white p-8 rounded-xl shadow-soft hover-lift animate-fade-in">
              <div className="w-16 h-16 rounded-full bg-dental-highlight/10 flex items-center justify-center mb-6">
                <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-dental-highlight"><path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z"></path><circle cx="12" cy="12" r="3"></circle></svg>
              </div>
              <h2 className="text-2xl font-serif font-semibold mb-4">Our Vision</h2>
              <p className="text-dental-muted mb-6">
                To be the premier dental care provider, recognized for excellence, innovation, and a steadfast commitment to patient-centered care. We aspire to build a community where everyone has access to exceptional dental care and the confidence of a healthy, radiant smile.
              </p>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <Check size={20} className="mr-3 text-dental-highlight flex-shrink-0 mt-1" />
                  <span className="text-dental-muted">Lead the way in pioneering advanced, comfortable, and effective dental treatments</span>
                </li>
                <li className="flex items-start">
                  <Check size={20} className="mr-3 text-dental-highlight flex-shrink-0 mt-1" />
                  <span className="text-dental-muted">Champion oral health education to empower our community</span>
                </li>
                <li className="flex items-start">
                  <Check size={20} className="mr-3 text-dental-highlight flex-shrink-0 mt-1" />
                  <span className="text-dental-muted">Foster positive, lifelong associations with dental care for patients of all ages</span>
                </li>
              </ul>
            </div>
            
            <div className="bg-white p-8 rounded-xl shadow-soft hover-lift animate-fade-in" style={{ animationDelay: "0.2s" }}>
              <div className="w-16 h-16 rounded-full bg-dental-accent/10 flex items-center justify-center mb-6">
                <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-dental-accent"><path d="M20.42 4.58a5.4 5.4 0 0 0-7.65 0l-.77.78-.77-.78a5.4 5.4 0 0 0-7.65 0C1.46 6.7 1.33 10.28 4 13l8 8 8-8c2.67-2.72 2.54-6.3.42-8.42z"></path></svg>
              </div>
              <h2 className="text-2xl font-serif font-semibold mb-4">Our Mission</h2>
              <p className="text-dental-muted mb-6">
                At Asrani Dental, our mission is to provide exceptional dental care that enhances our patients' well-being and quality of life. We are committed to fostering a welcoming environment where every patient feels valued, understood, and empowered to take charge of their oral health.
              </p>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <Check size={20} className="mr-3 text-dental-accent flex-shrink-0 mt-1" />
                  <span className="text-dental-muted">Deliver personalized care tailored to each patient's unique needs</span>
                </li>
                <li className="flex items-start">
                  <Check size={20} className="mr-3 text-dental-accent flex-shrink-0 mt-1" />
                  <span className="text-dental-muted">Uphold the highest standards of clinical excellence, safety, and innovation</span>
                </li>
                <li className="flex items-start">
                  <Check size={20} className="mr-3 text-dental-accent flex-shrink-0 mt-1" />
                  <span className="text-dental-muted">Build lasting relationships founded on trust, respect, and open communication</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>
      
      {/* Values Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container-custom">
          <SectionTitle
            subtitle="Our Core Values"
            title="The Principles That Guide Us"
            description="These fundamental values shape how we approach every aspect of dental care and patient interaction."
          />
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="p-6 border border-dental-light/20 rounded-xl hover-lift animate-fade-in">
              <div className="w-14 h-14 flex items-center justify-center mb-5 text-dental-highlight">
                <span className="text-5xl font-serif font-light">01</span>
              </div>
              <h3 className="text-xl font-serif font-medium mb-3">Excellence</h3>
              <p className="text-dental-muted">We strive for excellence in all aspects of our practice, from clinical care to patient communication and service.</p>
            </div>
            
            <div className="p-6 border border-dental-light/20 rounded-xl hover-lift animate-fade-in" style={{ animationDelay: "0.1s" }}>
              <div className="w-14 h-14 flex items-center justify-center mb-5 text-dental-highlight">
                <span className="text-5xl font-serif font-light">02</span>
              </div>
              <h3 className="text-xl font-serif font-medium mb-3">Compassion</h3>
              <p className="text-dental-muted">We approach each patient with genuine empathy, understanding, and a desire to provide comfort and care.</p>
            </div>
            
            <div className="p-6 border border-dental-light/20 rounded-xl hover-lift animate-fade-in" style={{ animationDelay: "0.2s" }}>
              <div className="w-14 h-14 flex items-center justify-center mb-5 text-dental-highlight">
                <span className="text-5xl font-serif font-light">03</span>
              </div>
              <h3 className="text-xl font-serif font-medium mb-3">Integrity</h3>
              <p className="text-dental-muted">We conduct ourselves with honesty, transparency, and a commitment to doing what's right for our patients.</p>
            </div>
            
            <div className="p-6 border border-dental-light/20 rounded-xl hover-lift animate-fade-in" style={{ animationDelay: "0.3s" }}>
              <div className="w-14 h-14 flex items-center justify-center mb-5 text-dental-highlight">
                <span className="text-5xl font-serif font-light">04</span>
              </div>
              <h3 className="text-xl font-serif font-medium mb-3">Innovation</h3>
              <p className="text-dental-muted">We embrace advances in dental technology and techniques to provide the most effective and comfortable care possible.</p>
            </div>
            
            <div className="p-6 border border-dental-light/20 rounded-xl hover-lift animate-fade-in" style={{ animationDelay: "0.4s" }}>
              <div className="w-14 h-14 flex items-center justify-center mb-5 text-dental-highlight">
                <span className="text-5xl font-serif font-light">05</span>
              </div>
              <h3 className="text-xl font-serif font-medium mb-3">Respect</h3>
              <p className="text-dental-muted">We value the diversity of our patients and team members, treating everyone with dignity and respect at all times.</p>
            </div>
            
            <div className="p-6 border border-dental-light/20 rounded-xl hover-lift animate-fade-in" style={{ animationDelay: "0.5s" }}>
              <div className="w-14 h-14 flex items-center justify-center mb-5 text-dental-highlight">
                <span className="text-5xl font-serif font-light">06</span>
              </div>
              <h3 className="text-xl font-serif font-medium mb-3">Education</h3>
              <p className="text-dental-muted">We believe in empowering our patients through education, helping them make informed decisions about their oral health.</p>
            </div>
          </div>
        </div>
      </section>
      
      {/* Team Preview Section */}
      <section id="team-section" className="py-16 md:py-24 bg-dental-cream">
        <div className="container-custom">
          <SectionTitle
            subtitle="Our Team"
            title="Meet the Experts Behind Your Smile"
            description="Our skilled team of dental professionals is dedicated to providing you with exceptional care in a warm and welcoming environment."
          />
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <DoctorCard
              name="Dr. Hemant Asrani"
              role="MDS - Endodontist"
              image="/lovable-uploads/efdb41d1-0ab8-4227-902b-10b6a3b129af.png"
              description="Dr. Hemant Asrani is a highly skilled dental surgeon specializing as an endodontist, renowned for his expertise in complex procedures and dedication to quality patient care in Nagpur."
            />
            
            <DoctorCard
              name="Dr. Karan Asrani"
              role="MDS - Periodontist"
              image="/lovable-uploads/499739a7-d200-4ee3-8350-050c6190d986.png"
              description="Dr. Karan Asrani is a skilled periodontist specializing in implantology and full mouth rehabilitation. He offers comprehensive dental care using advanced techniques to ensure optimal oral health."
            />
            
            <DoctorCard
              name="Dr. Nidhi Asrani"
              role="BDS, MA"
              image="/lovable-uploads/1be6e61b-063b-4ca2-8165-26aa828b7a14.png"
              description="Dr. Nidhi Asrani is a skilled dentist, recognized for her expertise in delivering comprehensive dental care. She prioritizes creating a comfortable and supportive environment, ensuring patients receive personalized and effective treatment."
            />
            
            <DoctorCard
              name="Dr. Aishwarya Deshmukh"
              role="MDS - Cosmetic Dentist"
              image="/lovable-uploads/762aed2d-c93c-4143-bd1e-b77314abdd09.png"
              description="Dr. Aishwarya Deshmukh is passionate about cosmetic dentistry, helping patients achieve beautiful, natural-looking smiles through advanced techniques."
            />
          </div>
        </div>
      </section>
      
      {/* CTA Section */}
      <CTASection
        title="Experience Our Difference"
        description="Schedule your visit today and discover dental care that puts you first."
        primaryButtonText="Book an Appointment"
        secondaryButtonText="Contact Us"
        primaryButtonLink="/dental-appointment-booking"
        secondaryButtonLink="/contact"
      />
      
      <Footer />
    </div>
  );
};

export default About;
